<template>
  <ts-modal
    ref="modalComponent"
    :modal-title="modalTitle"
    :modal-class="modalClassWithForm"
    :modal-dialog-class="modalDialogClass"
    :keyboard="false"
  >
    <template #modal-content>
      <loading-indicator
        v-if="isLoading"
        :message="loadingMessage"
        :vertical="true"
        additional-css-class="mt-3"
      />
      <div
        v-if="error && showModalError"
        class="alert alert-danger"
        role="alert"
      >
        {{ error }}
      </div>
      <slot name="modal-content"/>
      <div
        v-if="showForm"
        ref="formWrap"
        v-html="formHTML"
      />
    </template>
    <template #modal-footer>
      <slot name="modal-footer">
        <button
          v-if="deleteVisible"
          type="button"
          class="btn btn-sm btn-danger"
          @click="deleteItem"
        >
          Delete
        </button>
        <button
          type="button"
          class="btn btn-sm btn-secondary"
          data-bs-dismiss="modal"
        >
          Close
        </button>
        <button
          v-if="showFormSubmit"
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="isLoading"
          @click="submitForm"
        >
          Save
        </button>
      </slot>
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, useTemplateRef, type PropType} from 'vue'
import axios from 'axios'
import Rails from '@rails/ujs'

import LoadingIndicator from './loading-indicator.vue'
import TsModal from './ts-modal.vue'

import {initializeFormFields, registerFieldsByTagsTriggers, updateFieldsByTags} from '../site-utils'
import {isNonEmptyString, isPresent} from '../js-utils'

export interface PerformRequestCB<T> {
  (status: boolean, responseData?: T): void;
}

const props = defineProps({
  modalTitle: {default: '', type: String},
  showModalError: {default: true, type: Boolean},
  modalClass: {default: null, type: String},
  modalDialogClass: {default: null, type: String},
  formUrl: {default: null, type: String as PropType<string | null>},
  formParams: {default: null, type: Object},
  dataUrl: {default: null, type: String},
  dataParams: {default: null, type: String}
})

const emit = defineEmits<{
  'data-loaded': [status: boolean, data?: unknown]
  'success': [candidate?: unknown]
  'remote-form-loaded': []
}>()

const modalComponent = useTemplateRef<InstanceType<typeof TsModal>>('modalComponent')
const formWrap = useTemplateRef<HTMLElement>('formWrap')

const isLoading = ref(false)
const loadingMessage = ref<string | null>(null)
const error = ref<string | null>(null)
const axiosAbortController = ref<AbortController | null>(null)
const formHTML = ref<string | null>(null)
const formEl = ref<HTMLElement | null>(null)
const deleteUrl = ref<string | null>(null)

const modalClassWithForm = computed((): string => {
  if (isPresent(props.modalClass)) {
    return `modal-form ${props.modalClass}`
  }
  return 'modal-form'
})

const hasForm = computed((): boolean => isPresent(props.formUrl))

const showForm = computed((): boolean => hasForm.value && isPresent(formHTML.value))

const showFormSubmit = computed((): boolean => showForm.value && !!formEl.value)

const deleteVisible = computed((): boolean => isPresent(deleteUrl.value))

const showLoadingIndicator = (show: boolean, message?: string): void => {
  isLoading.value = show
  if (isNonEmptyString(message)) {
    loadingMessage.value = message
  } else {
    loadingMessage.value = null
  }
}

const hideLoadingIndicator = (): void => {
  isLoading.value = false
}

const showError = (message: string): void => {
  error.value = message
}

const hideError = (): void => {
  error.value = null
}

const showModal = (): void => {
  if (!modalComponent.value?.isOpen) {
    error.value = null

    modalComponent.value?.show()

    nextTick(() => {
      if (hasForm.value) {
        loadForm()
      } else if (isPresent(props.dataUrl)) {
        loadData()
      }
    })
  }
}

const hideModal = (): void => {
  modalComponent.value?.hide()
}

const loadForm = (): void => {
  if (!isNonEmptyString(props.formUrl)) {
    return
  }

  formHTML.value = null
  formEl.value = null
  hideError()

  showLoadingIndicator(true, 'Loading form ...')

  axios(props.formUrl, {
    method: props.formParams ? 'post' : 'get',
    data: props.formParams,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': Rails.csrfToken()
    }
  })
    .then((response) => {
      setupForm(response.data)
    })
    .catch((requestError) => {
      showError(`An error occurred while loading data: ${requestError.message}`)
    })
    .then(() => {
      hideLoadingIndicator()
    })
}

const loadData = (): void => {
  if (!props.dataUrl) {
    return
  }
  performRequest(props.dataUrl, props.dataParams, {method: 'get', jsonapi: true}, (status, data) => {
    emit('data-loaded', status, data)
  })
}

const attachToFormEvents = (): void => {
  if (!(formWrap.value instanceof HTMLElement)) {
    return
  }

  formEl.value = formWrap.value.querySelector(':scope form[ref="inputForm"]')

  if (formEl.value && formEl.value.getAttribute('data-form-initialized') !== 'true') {
    // need to set initialized attribute, so attachment to events
    // does not happen multiple times if formHTML does not change
    // between the requests
    formEl.value.setAttribute('data-form-initialized', 'true')

    // force request to be json
    formEl.value.setAttribute('data-type', 'json')

    // initialize form
    initializeFormFields(formEl.value)

    // attach to events
    formEl.value.addEventListener('ajax:beforeSend', () => {
      showLoadingIndicator(true)
      return true
    })
    formEl.value.addEventListener('ajax:success', (event: any) => {
      const [data] = event.detail
      setupForm(data)
      showLoadingIndicator(false)
    })
    formEl.value.addEventListener('ajax:error', (event: any) => {
      const [, status, xhr] = event.detail
      alert(`Error: ${xhr.status} - ${status}`)
      showLoadingIndicator(false)
    })
  }
}

const setupForm = (responseData: any): void => {
  deleteUrl.value = null
  if (typeof responseData === 'string') {
    formHTML.value = responseData
  } else {
    const status = responseData.status
    if (isPresent(status) && status === 'success') {
      // form operation succeeded
      emit('success', responseData.candidate)
      return
    }
    formHTML.value = responseData.html
    if (isPresent(responseData.deleteUrl)) {
      deleteUrl.value = responseData.deleteUrl
    }
  }

  nextTick(() => {
    attachToFormEvents()
    const formElement = formWrap.value?.querySelector('form')
    if (formElement) {
      updateFieldsByTags(formElement)
      registerFieldsByTagsTriggers(formElement)
    }
    emit('remote-form-loaded')
  })
}

/**
 * @fixme completedCallback types, options types
 */
const performRequest = <T>(url: string, params: any, options: any, completedCallback: PerformRequestCB<T>): void => {
  // TODO: change to return Promise and remove the completedCallback argument

  // cancel previous load request
  axiosAbortController.value?.abort()
  axiosAbortController.value = new AbortController()

  const headers: Record<string, string | null> = {
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-Token': Rails.csrfToken()
  }

  const requestOptions = Object.assign({
    method: 'post',
    jsonapi: false,
    withLoadingIndicator: true,
    clearError: true,
  }, options || {})

  if (requestOptions.jsonapi === true) {
    headers['Accept'] = 'application/vnd.api+json'
  }

  if (requestOptions.withLoadingIndicator) {
    showLoadingIndicator(true)
  }

  if (requestOptions.clearError) {
    hideError()
  }

  axios(url, {
    method: requestOptions.method,
    data: params,
    headers: headers,
    signal: axiosAbortController.value.signal,
    onUploadProgress: (progressEvent) => {
      if (requestOptions.progressCallback) {
        const uploadProgress = Math.floor(progressEvent.loaded / progressEvent.total! * 100)
        requestOptions.progressCallback(uploadProgress, progressEvent.loaded === progressEvent.total)
      }
    }
  })
    .then((response) => {
      // this.$emit('requestCompleted', response.data)
      completedCallback(true, response?.data)
    })
    .catch((requestError) => {
      if (axios.isCancel(requestError)) {
        // request was canceled
      } else {
        showError(`An error occurred while loading items: ${requestError.message}`)
        // this.$emit('requestFailed')
        completedCallback(false)
      }
    })
    .finally(() => {
      if (requestOptions.withLoadingIndicator) {
        hideLoadingIndicator()
      }
    })
}

const submitForm = (): void => {
  if (formEl.value) {
    Rails.fire(formEl.value, 'submit')
  } else {
    alert('Form not initialized')
  }
}

const deleteItem = (): void => {
  if (isNonEmptyString(deleteUrl.value)) {
    if (!confirm('Delete?')) {
      return
    }
    performRequest<{ error?: string }>(deleteUrl.value, null, {method: 'DELETE'}, (status, data) => {
      if (status) {
        if (data && isNonEmptyString(data.error)) {
          showError(data.error)
        } else {
          emit('success')
        }
      }
    })
  }
}

defineExpose({
  hideError,
  hideLoadingIndicator,
  hideModal,
  performRequest,
  setupForm,
  showError,
  showLoadingIndicator,
  showModal,
})
</script>
